import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import {
  GitBranch,
  Clock,
  MousePointer,
  UserCheck,
  ShieldCheck,
  DollarSign,
  XCircle,
  AlertTriangle,
  Plus,
  RefreshCw,
  ChevronDown,
  Send,
  Expand,
  Timer,
  Loader2,
  Filter
} from 'lucide-react';
import { formatINR } from '@/utils/currency';
import BrokerSidebar from '@/components/broker/BrokerSidebar';
import AssignmentModal from '@/components/broker/AssignmentModal';
import { Button } from '@/components/ui/button';
import { MOCK_BROKER_PIPELINE } from '@/mocks/brokerMocks';
import { useMockData as shouldUseMockData } from '@/mocks/config';

const STATUS_CONFIG = {
  PENDING:    { label: 'Pending',    color: 'border-gray-500 text-gray-400 bg-gray-500/10', icon: Clock },
  CLICKED:    { label: 'Clicked',    color: 'border-blue-500 text-blue-400 bg-blue-500/10', icon: MousePointer },
  REGISTERED: { label: 'Registered', color: 'border-cyan-500 text-cyan-400 bg-cyan-500/10', icon: UserCheck },
  KYC_DONE:   { label: 'KYC Done',   color: 'border-purple-500 text-purple-400 bg-purple-500/10', icon: ShieldCheck },
  CONVERTED:  { label: 'Converted',  color: 'border-[#4ADE80] text-[#4ADE80] bg-[#4ADE80]/10', icon: DollarSign },
  EOI_PAID:   { label: 'Converted',  color: 'border-[#4ADE80] text-[#4ADE80] bg-[#4ADE80]/10', icon: DollarSign },
  EXPIRED:    { label: 'Expired',    color: 'border-yellow-500 text-yellow-400 bg-yellow-500/10', icon: AlertTriangle },
  CANCELLED:  { label: 'Cancelled',  color: 'border-red-500 text-red-400 bg-red-500/10', icon: XCircle },
};

function FunnelCard({ label, count, amount, icon: Icon, color }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/5 p-4 rounded-sm">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-white/40 uppercase tracking-wider">{label}</span>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <p className="text-2xl font-mono font-medium text-white">{count}</p>
      {amount > 0 && <p className="text-xs font-mono text-white/30 mt-1">{formatINR(amount)}</p>}
    </div>
  );
}

function AssignmentRow({ assignment, onExtend, onCancel, onRemind }) {
  const config = STATUS_CONFIG[assignment.status] || STATUS_CONFIG.PENDING;
  const StatusIcon = config.icon;
  const isActive = ['PENDING', 'CLICKED', 'REGISTERED', 'KYC_DONE'].includes(assignment.status);

  const expiresAt = assignment.expiresAt ? new Date(assignment.expiresAt) : null;
  const now = new Date();
  const hoursLeft = expiresAt ? Math.max(0, Math.round((expiresAt - now) / 3600000)) : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#0A0A0A] border border-white/5 p-4 rounded-sm hover:border-white/10 transition-colors"
    >
      <div className="flex items-center justify-between gap-4">
        {/* Investor info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white truncate">{assignment.investorName || assignment.investorPhone}</p>
          <p className="text-xs font-mono text-white/30 mt-0.5">{assignment.investorPhone}</p>
        </div>

        {/* Unit */}
        <div className="hidden sm:block text-right">
          <p className="text-xs text-white/50">{assignment.projectName}</p>
          <p className="text-xs font-mono text-white/30">{assignment.unitNumber}</p>
        </div>

        {/* Amount */}
        <div className="text-right min-w-[80px]">
          <p className="text-sm font-mono text-white">{formatINR(assignment.assignedAmount)}</p>
          <p className="text-[10px] font-mono text-white/25">{assignment.ownershipPercentage?.toFixed(2)}%</p>
        </div>

        {/* Status badge */}
        <div className={`flex items-center gap-1.5 px-2.5 py-1 border rounded-sm text-xs ${config.color}`}>
          <StatusIcon className="w-3 h-3" />
          <span>{config.label}</span>
        </div>

        {/* Time left */}
        {isActive && hoursLeft !== null && (
          <div className="hidden md:flex items-center gap-1 text-xs min-w-[60px]">
            <Timer className={`w-3 h-3 ${hoursLeft < 12 ? 'text-yellow-400' : 'text-white/30'}`} />
            <span className={`font-mono ${hoursLeft < 12 ? 'text-yellow-400' : 'text-white/30'}`}>
              {hoursLeft}h
            </span>
          </div>
        )}

        {/* Actions */}
        {isActive && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => onRemind(assignment.assignmentId)}
              className="p-1.5 text-white/30 hover:text-blue-400 transition-colors"
              title="Send Reminder"
              data-testid={`remind-${assignment.assignmentId}`}
            >
              <Send className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onExtend(assignment.assignmentId)}
              className="p-1.5 text-white/30 hover:text-[#4ADE80] transition-colors"
              title="Extend 24h"
              data-testid={`extend-${assignment.assignmentId}`}
            >
              <Expand className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onCancel(assignment.assignmentId)}
              className="p-1.5 text-white/30 hover:text-red-400 transition-colors"
              title="Cancel"
              data-testid={`cancel-${assignment.assignmentId}`}
            >
              <XCircle className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default function BrokerPipeline() {
  const [assignments, setAssignments] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [cancelledInvestments, setCancelledInvestments] = useState([]);
  const [showCancelled, setShowCancelled] = useState(false);

  const fetchData = useCallback(async () => {
    const isMockMode = shouldUseMockData('broker', 'pipeline');
    
    setLoading(true);
    
    if (isMockMode) {
      // Use mock data
      let filteredAssignments = MOCK_BROKER_PIPELINE.assignments;
      if (filterStatus) {
        filteredAssignments = filteredAssignments.filter(a => a.status === filterStatus);
      }
      setAssignments(filteredAssignments);
      setFunnel({ funnel: MOCK_BROKER_PIPELINE.funnel });
      setLoading(false);
      return;
    }
    
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;

      const [assignRes, funnelRes] = await Promise.all([
        api.get('/broker/pipeline/assignments', { params }),
        api.get('/broker/pipeline/funnel')
      ]);

      setAssignments(assignRes.data.assignments || []);
      setFunnel(funnelRes.data.funnel || null);

      // Fetch cancelled investments
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const cancelledRes = await api.get(`/broker/${user.id || user.userId}/cancelled-investments`);
        setCancelledInvestments(cancelledRes.data?.investments || []);
      } catch { /* silently ignore */ }
    } catch (err) {
      if (err.response?.status !== 401) {
        console.error('Pipeline API error, using mock data:', err);
        // Fallback to mock data
        let filteredAssignments = MOCK_BROKER_PIPELINE.assignments;
        if (filterStatus) {
          filteredAssignments = filteredAssignments.filter(a => a.status === filterStatus);
        }
        setAssignments(filteredAssignments);
        setFunnel({ funnel: MOCK_BROKER_PIPELINE.funnel });
      }
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExtend = async (id) => {
    try {
      await api.post(`/broker/pipeline/assignments/${id}/extend`, { hours: 24 });
      toast.success('Extended by 24 hours');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to extend');
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this assignment? The reservation will be released.')) return;
    try {
      await api.post(`/broker/pipeline/assignments/${id}/cancel`, { reason: 'Cancelled by broker' });
      toast.success('Assignment cancelled');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  const handleRemind = async (id) => {
    try {
      await api.post(`/broker/pipeline/assignments/${id}/remind`);
      toast.success('Reminder queued');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reminder');
    }
  };

  const funnelData = funnel?.funnel || {};

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      <BrokerSidebar />
      <main className="md:ml-64 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-light tracking-tight">Pipeline</h1>
            <p className="text-sm text-white/40 mt-1">Investor assignments & conversion tracking</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              disabled={loading}
              className="border-white/10 text-white/50 hover:text-white bg-transparent"
              data-testid="refresh-pipeline"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={() => setShowModal(true)}
              className="bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90"
              data-testid="create-assignment-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Create Assignment
            </Button>
          </div>
        </div>

        {/* Funnel Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          <FunnelCard label="Pending" count={funnelData.PENDING?.count || 0} amount={funnelData.PENDING?.amount || 0} icon={Clock} color="text-gray-400" />
          <FunnelCard label="Clicked" count={funnelData.CLICKED?.count || 0} amount={funnelData.CLICKED?.amount || 0} icon={MousePointer} color="text-blue-400" />
          <FunnelCard label="Registered" count={funnelData.REGISTERED?.count || 0} amount={funnelData.REGISTERED?.amount || 0} icon={UserCheck} color="text-cyan-400" />
          <FunnelCard label="KYC Done" count={funnelData.KYC_DONE?.count || 0} amount={funnelData.KYC_DONE?.amount || 0} icon={ShieldCheck} color="text-purple-400" />
          <FunnelCard label="Converted" count={funnelData.EOI_PAID?.count || 0} amount={funnelData.EOI_PAID?.amount || 0} icon={DollarSign} color="text-[#4ADE80]" />
          <FunnelCard label="Expired" count={funnelData.EXPIRED?.count || 0} amount={funnelData.EXPIRED?.amount || 0} icon={AlertTriangle} color="text-yellow-400" />
          <FunnelCard label="Cancelled" count={funnelData.CANCELLED?.count || 0} amount={funnelData.CANCELLED?.amount || 0} icon={XCircle} color="text-red-400" />
        </div>

        {/* Filter Bar */}
        <div className="flex items-center gap-3 mb-4">
          <Filter className="w-4 h-4 text-white/30" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-[#0A0A0A] border border-white/10 text-sm text-white/70 px-3 py-1.5 rounded-sm focus:outline-none focus:border-[#4ADE80]/50"
            data-testid="filter-status"
          >
            <option value="">All Statuses</option>
            <option value="ACTIVE">Active (In Progress)</option>
            <option value="PENDING">Pending</option>
            <option value="CLICKED">Clicked</option>
            <option value="REGISTERED">Registered</option>
            <option value="KYC_DONE">KYC Done</option>
            <option value="CONVERTED">Converted (EOI Paid)</option>
            <option value="LOST">Lost (Expired/Cancelled)</option>
          </select>
          <span className="text-xs text-white/30 font-mono">{assignments.length} assignments</span>
        </div>

        {/* Assignments List */}
        <div className="space-y-2" data-testid="assignments-list">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-white/30" />
            </div>
          ) : assignments.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-white/10 rounded-sm" data-testid="empty-state">
              <GitBranch className="w-10 h-10 text-white/10 mx-auto mb-3" />
              <p className="text-white/30 text-sm">No assignments yet</p>
              <p className="text-white/20 text-xs mt-1">Create your first assignment to start building your pipeline</p>
              <Button
                size="sm"
                onClick={() => setShowModal(true)}
                className="mt-4 bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90"
              >
                <Plus className="w-4 h-4 mr-1" /> Create Assignment
              </Button>
            </div>
          ) : (
            assignments.map((a) => (
              <AssignmentRow
                key={a.assignmentId}
                assignment={a}
                onExtend={handleExtend}
                onCancel={handleCancel}
                onRemind={handleRemind}
              />
            ))
          )}
        </div>
      </main>

      {/* Cancelled Referrals Section */}
      {cancelledInvestments.length > 0 && (
        <div className="max-w-6xl mx-auto px-6 mt-6 mb-6">
          <button
            onClick={() => setShowCancelled(!showCancelled)}
            className="flex items-center gap-2 text-sm text-red-400/70 hover:text-red-400 transition-colors mb-3"
            data-testid="toggle-cancelled-referrals"
          >
            <XCircle className="w-4 h-4" />
            Cancelled Referrals ({cancelledInvestments.length})
            <ChevronDown className={`w-3 h-3 transition-transform ${showCancelled ? 'rotate-180' : ''}`} />
          </button>
          {showCancelled && (
            <div className="space-y-2">
              {cancelledInvestments.map(inv => (
                <div key={inv.investmentId} className="border border-red-500/20 bg-red-500/5 p-4" data-testid={`cancelled-inv-${inv.investmentId}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-white">{inv.investorName} — {inv.unitName || inv.investmentId}</p>
                      <p className="text-xs text-gray-600">{inv.projectName}</p>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 bg-red-500/20 text-red-400">
                      {inv.state === 'CANCELLATION_REQUESTED' ? 'PENDING' : 'CANCELLED'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-xs">
                    <div>
                      <p className="text-gray-600">Committed</p>
                      <p className="font-mono text-white">{formatINR(inv.originalCommitment)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Paid / Refunded</p>
                      <p className="font-mono text-gray-400">{formatINR(inv.capitalPaid)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Reason</p>
                      <p className="text-gray-300">{inv.cancelReason?.replace(/_/g, ' ') || '—'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Commission Voided</p>
                      <p className="font-mono text-red-400">{formatINR(inv.commissionVoided)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Assignment Modal */}
      {showModal && (
        <AssignmentModal
          onClose={() => setShowModal(false)}
          onCreated={() => { setShowModal(false); fetchData(); }}
        />
      )}
    </div>
  );
}
